import React from 'react';
import {
	Box,
	Typography,
	Button,
	useTheme,
	SwipeableDrawer,
	Divider,
	Alert,
	useMediaQuery,
	IconButton,
} from '@mui/material';
import { BillingScheduleCollectionResponse } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import { useNavigate } from 'react-router';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { AccountBalance, Close } from '@mui/icons-material';
import Visa from '@acorn-portal/shared/src/assets/images/Visa.svg?react';
import Mastercard from '@acorn-portal/shared/src/assets/images/Mastercard.svg?react';
import PayPal from '@acorn-portal/shared/src/assets/images/PayPal.svg?react';
import { usePaymentSchedule } from './usePaymentSchedule';
import {
	isBillingBankAccountPaymentMethod,
	isBillingCardPaymentMethod,
	isBillingMSPPaymentMethod,
	isBillingWalletPaymentMethod,
} from '../../utils/payment-method-type-guard';
import { parseISO } from 'date-fns';

interface PaymentScheduleDetailsProps {
	open: boolean;
	onClose: () => void;
	collection: BillingScheduleCollectionResponse;
}

const headerStyles = { fontSize: '1.1rem', my: 1 };

const PaymentScheduleDetails: React.FC<PaymentScheduleDetailsProps> = ({ open, onClose, collection }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { getPayNowPaymentSchedule, getStatusOfSplitPaymentForThisPayment } = usePaymentSchedule([collection]);
	const navigate = useNavigate();
	const { policyToken } = useAppSelector((redux) => redux.fullPolicy);
	const payNowPath = `${ROUTE_PATHS.paymentPlan}/${policyToken}`;

	const formatDate = (date: Date | undefined): string => {
		return date ? new Date(date).toDateString() : 'N/A';
	};

	const getPaymentAlert = (collection: BillingScheduleCollectionResponse) => {
		switch (collection.status) {
			case 'AUTHORISED':
				return <Alert sx={{ my: 2 }}>This Payment has been collected.</Alert>;
			case 'FAILED_AND_REPLACED': {
				const status = getStatusOfSplitPaymentForThisPayment(collection);

				if (status === 'Active') {
					return (
						<Alert sx={{ my: 2 }} severity='info'>
							You entered into a payment plan for this direct debit payment.
						</Alert>
					);
				} else if (status === 'PaidOff' || status === 'NoPlan') {
					return (
						<Alert sx={{ my: 2 }} severity='info'>
							You have paid off this payment with a payment plan.
						</Alert>
					);
				}
				return;
			}
			case 'FAILED':
				return (
					<Alert sx={{ my: 2, cursor: 'pointer' }} severity='error' onClick={() => navigate(payNowPath)}>
						Your payment has failed, please click this here to enter into a payment plan.
					</Alert>
				);
			case 'OVERDUE':
				return (
					<Alert sx={{ my: 2, cursor: 'pointer' }} severity='error' onClick={() => navigate(payNowPath)}>
						Your payment is overdue, please click this here to enter into a payment plan.
					</Alert>
				);
			default:
				if (collection.scheduledDate !== collection.dueDate) {
					return (
						<Alert sx={{ my: 2 }} severity='info'>
							The collection date for this payment is different from your preferred date due to being on a weekend or
							bank holiday.
						</Alert>
					);
				}
				break;
		}
	};

	const getPaymentVendor = (brand: string) => {
		switch (brand.toLowerCase()) {
			case 'visa':
				return <Visa />;
			case 'mastercard':
				return <Mastercard />;
			case 'paypal':
				return <PayPal />;
			default:
				return;
		}
	};

	const collectionStartDate = parseISO(collection.scheduledDate);

	return (
		<SwipeableDrawer
			open={open}
			onClose={onClose}
			onOpen={() => {}}
			anchor='right'
			slotProps={{
				paper: {
					sx: {
						width: isMobile ? '100%' : '30%',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
					},
				},
			}}
		>
			<Box sx={{ m: 2, overflow: 'auto', flex: 1 }}>
				<Box sx={{ flex: 1, justifyContent: 'flex-end', display: 'flex' }}>
					<IconButton onClick={() => onClose()}>
						<Close />
					</IconButton>
				</Box>

				<Typography variant='h5' gutterBottom color='primary'>
					Payment Details
				</Typography>
				{getPaymentAlert(collection)}
				<Divider sx={{ my: 2 }} />

				{collection.status === 'FAILED_AND_REPLACED' || collection.status === 'OVERDUE' ? (
					<Box>
						<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
							Missed Direct Debit Date
						</Typography>
						<Typography variant='body1'>{formatDate(collectionStartDate)}</Typography>
						<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
							Missed Direct Debit Amount
						</Typography>
						<Typography variant='body1'>{formatCurrency(collection.originalAmount)}</Typography>
						{collection.feeAmount > 0 && (
							<>
								<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
									Admin Fee
								</Typography>
								<Typography variant='body1'>{formatCurrency(collection.feeAmount)}</Typography>
								<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
									Total Amount
								</Typography>
								<Typography variant='body1'>{formatCurrency(collection.amount)}</Typography>
							</>
						)}

						{getPayNowPaymentSchedule(collection)}
					</Box>
				) : (
					<>
						<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
							Collection Date
						</Typography>
						<Typography variant='body1'>{formatDate(collectionStartDate)}</Typography>
						<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
							Amount
						</Typography>
						<Typography variant='body1'>{formatCurrency(collection.amount)}</Typography>
					</>
				)}

				<Divider sx={{ my: 2 }} />
				{collection.paymentMethod && isBillingBankAccountPaymentMethod(collection.paymentMethod) && (
					<Box>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
							<Typography variant='subtitle1' color='secondary' sx={{ ...headerStyles, mt: 0, display: 'block' }}>
								Direct Debit Details
							</Typography>
							<Box
								sx={{
									svg: {
										width: '35px',
										height: '35px',
									},
								}}
							>
								<AccountBalance sx={{ path: { fill: theme.palette.primary.main } }} />
							</Box>
						</Box>
						<Box>
							<Typography variant='body1'>Sort Code: {collection.paymentMethod.sortCode}</Typography>
							<Typography variant='body1'>Account Number: {collection.paymentMethod.accountNumber}</Typography>
						</Box>
					</Box>
				)}
				{collection.paymentMethod &&
					(isBillingCardPaymentMethod(collection.paymentMethod) ||
						isBillingMSPPaymentMethod(collection.paymentMethod)) && (
						<Box>
							<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
								Card Payment Details
								<Box
									sx={{
										'& svg': { height: '40px', width: '60px' },
										flex: 1,
										justifyContent: 'flex-end',
										display: 'flex',
										mr: 3,
									}}
								>
									{getPaymentVendor(collection.paymentMethod.brand ?? '')}
								</Box>
							</Typography>

							<Typography variant='body1'>
								Card Number : {`**** **** **** ${collection.paymentMethod.last4Digits}`}
							</Typography>
						</Box>
					)}
				{collection.paymentMethod && isBillingWalletPaymentMethod(collection.paymentMethod) && (
					<Box>
						<Typography variant='subtitle1' color='secondary' sx={headerStyles}>
							{collection.paymentMethod.description} Payment Details
							<Box
								sx={{
									'& svg': { height: '40px', width: '60px' },
									flex: 1,
									justifyContent: 'flex-end',
									display: 'flex',
									mr: 3,
								}}
							>
								{getPaymentVendor(collection.paymentMethod.description ?? '')}
							</Box>
						</Typography>

						<Typography variant='body1'>Account : {collection.paymentMethod.identifier}</Typography>
					</Box>
				)}
			</Box>
			<Box sx={{ p: 2 }}>
				<Divider />
				<Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
					<Button onClick={onClose} variant='contained' fullWidth>
						Close
					</Button>
				</Box>
			</Box>
		</SwipeableDrawer>
	);
};

export default PaymentScheduleDetails;
